/**
 * Reads `Retry-After` as a whole number of seconds.
 *
 * The header is also allowed to carry an HTTP date, which is parsed back into a
 * delay from now. A missing, unparseable, or past value returns `undefined` so a
 * caller can tell "wait this long" apart from "no guidance".
 */
export function parseRetryAfter(headers: Headers): number | undefined {
  const header = headers.get('Retry-After');

  if (!header) {
    return undefined;
  }

  const seconds = Number(header);

  if (Number.isFinite(seconds)) {
    return seconds > 0 ? Math.ceil(seconds) : undefined;
  }

  const retryAt = Date.parse(header);

  if (Number.isNaN(retryAt)) {
    return undefined;
  }

  const delaySeconds = Math.ceil((retryAt - Date.now()) / 1000);

  return delaySeconds > 0 ? delaySeconds : undefined;
}

/**
 * Whether this error is the API refusing because the caller is over its rate
 * limit, paired with how long to wait when the server said.
 *
 * Read structurally rather than through `instanceof ApiError`, so an error that
 * crossed a boundary and lost its prototype still reports the same way.
 */
export function getRateLimitDelay(error: unknown): { isRateLimited: boolean; seconds?: number } {
  const candidate = error as { status?: number; retryAfterSeconds?: number } | undefined;

  if (candidate?.status !== 429) {
    return { isRateLimited: false };
  }

  return { isRateLimited: true, seconds: candidate.retryAfterSeconds };
}
